"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { MapPin, Mail, Phone, Clock, Users, Calendar } from "lucide-react";
import { useState } from "react";
const POLY_GREEN = "#154734";
const MUSTANG_GOLD = "#BD8B13";
export default function ContactInformation() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  const MAX_MESSAGE = 500;
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: ""
  });
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<null | {
    type: "success" | "error";
    msg: string;
  }>(null);
  const errors = {
    firstName: !form.firstName.trim() ? "First name is required." : "",
    lastName: !form.lastName.trim() ? "Last name is required." : "",
    email: !/^\S+@\S+\.\S+$/.test(form.email.trim()) ? "Valid email is required." : "",
    message: !form.message.trim() ? "Message is required." : ""
  };
  const hasErrors = Boolean(errors.firstName || errors.lastName || errors.email || errors.message);
  function updateField<K extends keyof typeof form>(key: K, value: string) {
    setForm(prev => ({
      ...prev,
      [key]: value
    }));
  }
  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus(null);
    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      phone: true,
      message: true
    });
    if (hasErrors) {
      setStatus({
        type: "error",
        msg: "Please fill in all required fields."
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const subject = encodeURIComponent("EILH Contact Form Submission");
      const body = encodeURIComponent(`First Name: ${form.firstName}\n` + `Last Name: ${form.lastName}\n` + `Email: ${form.email}\n` + `Phone: ${form.phone || "(not provided)"}\n\n` + `Message:\n${form.message}\n`);
      window.location.href = `mailto:esanc146@calpoly.edu?subject=${subject}&body=${body}`;
      setStatus({
        type: "success",
        msg: "Opening your email client…"
      });
      setForm({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        message: ""
      });
      setTouched({});
    } catch {
      setStatus({
        type: "error",
        msg: "Something went wrong. Please try again."
      });
    } finally {
      setIsSubmitting(false);
    }
  }
  return <section id="contact-information" ref={sectionRef} className="py-20 px-6 overflow-hidden md:px-12 lg:px-24" style={{
    backgroundColor: POLY_GREEN,
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(20px)",
    transition: "opacity 300ms ease-out, transform 300ms ease-out"
  }}>
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:4rem_4rem]" />
      </div>

      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold mb-4 !font-serif md:text-6xl" style={{
          color: MUSTANG_GOLD
        }}>Get In Touch</h2>
          <p className="text-white/80 text-xl max-w-2xl mx-auto">
            Have questions about the conference? We'd love to hear from you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* LEFT: Contact Information */}
          

          {/* RIGHT: Contact Form - Fixed Width Container */}
          <div className="w-full max-w-2xl mx-auto lg:mx-0" style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateX(0)" : "translateX(40px)",
          transition: "opacity 400ms ease-out 100ms, transform 400ms ease-out 100ms"
        }}>
            <div className="rounded-2xl overflow-hidden shadow-2xl border-2 border-white/10 !w-[886px]">
              {/* Header */}
              <div className="px-8 py-6" style={{
              backgroundColor: MUSTANG_GOLD
            }}>
                <h2 className="text-white text-3xl font-bold font-serif">
                  Contact Us
                </h2>
              </div>

              {/* Form */}
              <div className="px-8 py-8 bg-[#B8BCC4]">
                <p className="mb-8 font-serif font-medium text-black text-lg">
                  Any questions? Feel free to reach out and we'll get back to
                  you as soon as we can!
                </p>

                <form onSubmit={onSubmit} className="space-y-6">
                  {/* First + Last Name */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-black">
                        First Name *
                      </label>
                      <input value={form.firstName} onChange={e => updateField("firstName", e.target.value)} onBlur={() => setTouched(t => ({
                      ...t,
                      firstName: true
                    }))} className="w-full border-b-2 border-black/30 bg-transparent outline-none py-2 text-black focus:border-black transition-colors" />
                      {touched.firstName && errors.firstName ? <div className="mt-2 text-xs text-red-700 font-semibold">
                          {errors.firstName}
                        </div> : null}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2 text-black">
                        Last Name *
                      </label>
                      <input value={form.lastName} onChange={e => updateField("lastName", e.target.value)} onBlur={() => setTouched(t => ({
                      ...t,
                      lastName: true
                    }))} className="w-full border-b-2 border-black/30 bg-transparent outline-none py-2 text-black focus:border-black transition-colors" />
                      {touched.lastName && errors.lastName ? <div className="mt-2 text-xs text-red-700 font-semibold">
                          {errors.lastName}
                        </div> : null}
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-black">
                      Email Address *
                    </label>
                    <input type="email" value={form.email} onChange={e => updateField("email", e.target.value)} onBlur={() => setTouched(t => ({
                    ...t,
                    email: true
                  }))} className="w-full border-b-2 border-black/30 bg-transparent outline-none py-2 text-black focus:border-black transition-colors" />
                    {touched.email && errors.email ? <div className="mt-2 text-xs text-red-700 font-semibold">
                        {errors.email}
                      </div> : null}
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-black">
                      Phone Number
                    </label>
                    <input value={form.phone} onChange={e => updateField("phone", e.target.value)} onBlur={() => setTouched(t => ({
                    ...t,
                    phone: true
                  }))} className="w-full border-b-2 border-black/30 bg-transparent outline-none py-2 text-black focus:border-black transition-colors" />
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-black">
                      Message *
                    </label>
                    <textarea value={form.message} onChange={e => updateField("message", e.target.value.slice(0, MAX_MESSAGE))} onBlur={() => setTouched(t => ({
                    ...t,
                    message: true
                  }))} rows={6} className="w-full border-2 border-black/30 bg-transparent outline-none p-4 resize-none text-black focus:border-black transition-colors rounded-lg" />
                    <div className="mt-2 flex items-center justify-between">
                      {touched.message && errors.message ? <div className="text-xs text-red-700 font-semibold">
                          {errors.message}
                        </div> : <div />}
                      <div className="text-xs text-black/60">
                        {form.message.length} / {MAX_MESSAGE}
                      </div>
                    </div>
                  </div>

                  {/* Status Message */}
                  {status ? <div className={`text-sm font-semibold ${status.type === "success" ? "text-green-700" : "text-red-700"}`}>
                      {status.msg}
                    </div> : null}

                  {/* Submit Button */}
                  <button type="submit" disabled={isSubmitting} className="w-full text-white font-bold text-lg px-6 py-4 rounded-lg transition-all duration-300 hover:scale-105 disabled:opacity-60 disabled:hover:scale-100 shadow-lg" style={{
                  backgroundColor: MUSTANG_GOLD
                }}>
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}